// @ts-check

import { Loading3QuartersOutlined, WarningFilled, ZoomInOutlined, PlusCircleFilled, MinusCircleFilled, CalendarOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Col, List, Modal, notification, Tag, Divider, Row, Select, Skeleton, Space, Spin, Typography } from "antd";
import axios from "axios";
import { DayPilot } from "daypilot-pro-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import EventSchedulerControl from "../components/EventScheduler/EventScheduler";
import tr from "../i18n";
import { createPlanRequestBuilder, deletePlanRequestBuilder, transformDevicesToResourcesArray } from "../utils/SchedulerUtil";
import { WateringMapper } from "../utils/WateringMapper";
import { useDevice } from "./device-context";
import { useGarden } from "./garden-context";
import { usePlans } from "./plans-context";
const { Text } = Typography;

/** @type {import("../types/global").Day[]} */
const initialDays = [
  { id: 1, name: tr("Mo"), fullName: tr("Monday"), selected: true },
  { id: 2, name: tr("Tu"), fullName: tr("Tuesday"), selected: true },
  { id: 3, name: tr("We"), fullName: tr("Wednesday"), selected: true },
  { id: 4, name: tr("Th"), fullName: tr("Thursday"), selected: true },
  { id: 5, name: tr("Fr"), fullName: tr("Friday"), selected: true },
  { id: 6, name: tr("Sa"), fullName: tr("Saturday"), selected: true },
  { id: 0, name: tr("Su"), fullName: tr("Sunday"), selected: true },
];

const DAY_NAMES = {
  0: tr("Su"), 1: tr("Mon"), 2: tr("Tue"), 3: tr("Wed"),
  4: tr("Thu"), 5: tr("Fri"), 6: tr("Sat"),
};

const schedulerModes = {
  QUICK: "quick",
  ADVANCED_COMMON: "advanced_common",
  ADVANCED_PER_DAY: "advanced_per_day",
};

/** @remarks Migrated from SchedulerUtil.js to add types to avoid updating legacy code. */
function composeDateString(
  /** @type {{dateString: string; timeString: string}} */
  { dateString, timeString }
) {
  return dateString + "T" + timeString + ":00";
}

/** @remarks Migrated from SchedulerUtil.js to add types to avoid updating legacy code. */
function getDateAfter24Hours() {
  return new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
}

/** @remarks Migrated from SchedulerUtil.js to add types to avoid updating legacy code. */
function getISODateStringFromDate(/** @type {Date} */ date) {
  return date.toISOString().split("T")[0];
}

/** @remarks Migrated from SchedulerUtil.js to add types to avoid updating legacy code. */
function transformPlanResourceToEvent(
  /** @type {import("../types/global").PlanTimeSeries[]} */
  planTimes,
  /** @type {{ model: import("../types/global").DevicePlanResponse["model"], serial: import("../types/global").DevicePlanResponse["serial"] }} */
  resource,
  /** @type {import("../types/global").DevicePlanResponse["model"]} */
  bltPlansEditable
) {
  return planTimes
    .filter((time) => time.e !== time.s)
    .map((time) => {
      let startDateTime;
      let endDateTime;

      /**
       * When the start time falls on the previous day
       * start time will be "" (empty string)
       * we should set it to 00:00 same day
       * so that scheduler can interpret the event correctly
       */
      if (time.s === "") {
        startDateTime = composeDateString({
          dateString: getISODateStringFromDate(new Date()),
          timeString: "00:00",
        });
      } else {
        startDateTime = composeDateString({
          dateString: getISODateStringFromDate(new Date()),
          timeString: time.s,
        });
      }

      /**
       * When the end time falls on the next day
       * end time will be "" (empty string)
       * we should set it to 00:00 next day
       * so that scheduler can interpret the event correctly
       */
      if (time.e === "") {
        const dateFrom24Hours = getDateAfter24Hours();
        endDateTime = composeDateString({
          dateString: getISODateStringFromDate(dateFrom24Hours),
          timeString: "00:00",
        });
      } else {
        endDateTime = composeDateString({
          dateString: getISODateStringFromDate(new Date()),
          timeString: time.e,
        });
      }
      let customText = "";
      if (time.daysApplied != null && time.daysApplied.length < 7) {
        const activeNames = time.daysApplied?.map((id) => initialDays.find((d) => d.id === id)?.fullName).join(", ");
        customText = `${new Date(startDateTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}-${new Date(endDateTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })} (${activeNames})`;
      }

      /** @type {import("../types/global").SchedulerEvent} */
      const result = {
        id: `${resource.serial}_${time.id}_${time.v}`,
        resource: `${resource?.serial}_${time.v}`,
        start: startDateTime,
        end: endDateTime,
        status: time.status,
        text: customText,
        moveVDisabled: true,
        isQuickPlan: time.isQuickPlan,
        isWeeklyPlan: time.isWeekly,
        readOnly: !bltPlansEditable && resource.serial.startsWith("BLT"),
        deviceModel: resource.model
      };

      return result;
    });
}

/** @param {number[]} ids */
function daysLabel(ids) {
  const clean = (ids || []).filter((d) => d != null);
  if (clean.length === 0) return tr("no days");
  if (clean.length === 7) return tr("every day");
  return [1, 2, 3, 4, 5, 6, 0].filter((d) => clean.includes(d)).map((d) => DAY_NAMES[d]).join(", ");
}
/**
 * @param {import("../types/global").UncommittedRequest[]} changes
 * @param {import("../types/global").Day[]} days
 */
function buildSummary(changes, days) {
  const created = changes.filter((c) => c.type === "create_plan");
  const deleted = changes.filter((c) => c.type === "delete_plan");
  const dayChange = changes.find((c) => c.type === "update_watering_days");

  /** @type {Record<string, number>} */
  const createdByDays = {};
  for (const c of created) {
    const applied = c?.meta?.targetDays ?? [];
    const key = daysLabel(applied);
    createdByDays[key] = (createdByDays[key] || 0) + 1;
  }

  const quickPlanHit = deleted.some((c) => c?.meta?.isQuickPlan);
  const disabledDays = days.filter((d) => !d.selected).map((d) => d.id);

  // The "everything landed on one day" alarm: all created plans share a single non-full day-set.
  const createdKeys = Object.keys(createdByDays);
  const singleDaySuspicion =
    created.length >= 3 && createdKeys.length === 1 && createdKeys[0] !== tr("every day");

  return { createdByDays, createdCount: created.length, deletedCount: deleted.length, dayChange, disabledDays, quickPlanHit, singleDaySuspicion, singleDayLabel: createdKeys[0] };
}

/**
 * Detect overlapping plans on the same device valve (resource).
 * Two plans conflict when they share a resource, share at least one active day,
 * and their [start,end) time ranges overlap.
 * @param {(import("../types/global").SchedulerEvent | import("../types/global").DayEventItem)[]} events
 * @returns {{ resource: string, a: any, b: any, days: number[] }[]}
 */
function findPlanConflicts(events) {
  const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

  // Only consider live plans; skip deleted/preview ones.
  const live = (events || []).filter(
    (e) => e && e.resource && !e.preview && (e.status === "active" || e.status === "new")
  );

  // Normalize each event to minutes-from-midnight + its day set.
  const norm = live.map((e) => {
    const start = new Date(e.start);
    const end = new Date(e.end);
    const startMin = start.getHours() * 60 + start.getMinutes();
    // end on the next day (00:00) means "end of day" → 1440
    const endMin =
      end.getDate() !== start.getDate() ? 24 * 60 : end.getHours() * 60 + end.getMinutes();
    const days =
      Array.isArray(e.daysApplied) && e.daysApplied.length ? e.daysApplied : ALL_DAYS;
    return { e, resource: e.resource, startMin, endMin, days };
  });

  // Group by resource (valve) so we only compare within the same valve.
  /** @type {Record<string, typeof norm>} */
  const byResource = {};
  for (const n of norm) (byResource[n.resource] ||= []).push(n);

  const conflicts = [];
  for (const resource of Object.keys(byResource)) {
    const group = byResource[resource];
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const a = group[i];
        const b = group[j];
        const sharedDays = a.days.filter((d) => b.days.includes(d));
        if (sharedDays.length === 0) continue; // different days → no conflict
        // half-open overlap: [aStart, aEnd) ∩ [bStart, bEnd)
        const overlaps = a.startMin < b.endMin && b.startMin < a.endMin;
        if (overlaps) conflicts.push({ resource, a: a.e, b: b.e, days: sharedDays });
      }
    }
  }
  return conflicts;
}

/**
 * @param {{
 *  open: boolean, onConfirm: () => void, onCancel: () => void, confirmLoading?: boolean,
 *  changes: import("../types/global").UncommittedRequest[], days: import("../types/global").Day[],
 * }} props
 */
export default function CommitReviewModal({ open, onConfirm, onCancel, confirmLoading, changes, days }) {
  const s = buildSummary(changes, days);
  const hasWarnings = s.quickPlanHit || s.disabledDays.length > 0 || s.singleDaySuspicion;

  return (
    <Modal
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      okText={tr("Confirm & Save")}
      cancelText={tr("Go back")}
      okButtonProps={{ danger: hasWarnings }}
      title={tr("Review changes before saving")}
      width={460}
    >
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
        {s.singleDaySuspicion && (
          <Warning>
            {tr("All new plans apply only to")}{" "}
            <Text strong>{s.singleDayLabel}</Text>.{" "}
          </Warning>
        )}

        {s.disabledDays.length > 0 && (
          <Warning>
            {tr("These days are disabled. No irrigation will be done on")}:{" "}
            <Text strong>{daysLabel(s.disabledDays)}</Text>
          </Warning>
        )}

        {s.quickPlanHit && (
          <Warning>
            {tr("A quick-plan section was removed. The quick plan will be split into regular plans.")}
          </Warning>
        )}

        {hasWarnings && <Divider style={{ margin: "10px 0" }} />}

        <Space direction="vertical" size={6} style={{ width: "100%" }}>
          {s.createdCount > 0 && (
            <div>
              <PlusCircleFilled style={{ color: "#27ae60", marginRight: 6 }} />
              <Text strong>{tr("{count} plan(s) added").replace("{count}", s.createdCount)}</Text>
              <div style={{ marginLeft: 22, marginTop: 2 }}>
                {Object.entries(s.createdByDays).map(([label, count]) => (
                  <div key={label}>
                    <Text type="secondary">{count}×</Text>{" "}
                    <Tag icon={<CalendarOutlined />} color="green" style={{ marginInlineEnd: 0 }}>{label}</Tag>
                  </div>
                ))}
              </div>
            </div>
          )}

          {s.deletedCount > 0 && (
            <div>
              <MinusCircleFilled style={{ color: "#c0392b", marginRight: 6 }} />
              <Text strong>{tr("{count} plan(s) removed").replace("{count}", s.deletedCount)}</Text>
            </div>
          )}

          {s.dayChange && (
            <div>
              <CalendarOutlined style={{ color: "#27ae60", marginRight: 6 }} />
              <Text strong>{tr("Active days set to")}:</Text>{" "}
              <Text>{daysLabel(s.dayChange.data.wateringDays)}</Text>
            </div>
          )}
        </Space>
      </div>
    </Modal>
  );
}

/** @param {import("react").PropsWithChildren<{}>} props */
function Warning({ children }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
      <WarningFilled style={{ color: "#fa8c16", marginTop: 2 }} />
      <div>{children}</div>
    </div>
  );
}

const EventSchedulerContext = /** @type {import("react").Context<import("../types/global").EventSchedulerContextValue | undefined>} */ (createContext(undefined));

export function useEventScheduler() {
  const context = /** @type {import("../types/global").EventSchedulerContextValue | undefined} */ (useContext(EventSchedulerContext));
  if (context === undefined) throw new Error("useEventScheduler must be used within an EventSchedulerProvider");
  return context;
}

/**
 * @param {import("react").PropsWithChildren<{}>} props
 * @returns {import("react").ReactElement | null}
 */
export function EventSchedulerProvider({ children }) {
  const { garden, gardenId } = useGarden();
  const { devices } = useDevice();
  const [days, setDays] = useState(initialDays);
  const [selectedDayId, setSelectedDayId] = /** @type {import("../types/global").SelectedDayIdStateType} */ (useState(undefined));
  const [isBluetoothPlansEditable, setBltPlansEditable] = useState(false);
  const [cellDuration, setCellDuration] = useState(5);
  const [events, setEvents] = useState(/** @type {(import("../types/global").SchedulerEvent | import("../types/global").DayEventItem)[]} */([]));
  const [dayEvents, setDayEvents] = useState(/** @type {import("../types/global").DayEvent | undefined} */(undefined));
  const { uncommittedChangesState, gardenPlanTemplatePreview, lastPreviewedTemplateId, onLoadGardenTemplate } = usePlans();
  const [, setUncommittedChanges] = uncommittedChangesState;
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewEvents, setPreviewEvents] = useState(/** @type {(import("../types/global").SchedulerEvent)[] | null} */(null));
  const queryClient = useQueryClient();

  /**
   * Return a Date for this week's weekday (0=Sunday), based on today's week.
   * @param {number} weekdayIndex
   * @returns {Date}
   */
  function getDateForWeekday(weekdayIndex) {
    const today = new Date();
    const todayIndex = today.getDay(); // 0..6
    const diff = (weekdayIndex - todayIndex + 7) % 7;
    const target = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diff);
    return target;
  }

  /**
   * Convert garden template preview into scheduler events (uses same date formatting as transformPlanResourceToEvent).
   * @param {import("../types/global").GardenPlanTemplatePreviewApiResponse} preview
   * @returns {import("../types/global").SchedulerEvent[]}
   */
  function convertGardenPreviewToEvents(preview) {
    if (!preview || !Array.isArray(preview.devices)) return [];

    const /** @type {import("../types/global").SchedulerEvent[]} */ results = [];

    preview.devices.forEach((device) => {
      const deviceSerial = device.deviceSerial;

      (device.plans || []).forEach((plan) => {
        (plan.times || []).forEach((t) => {
          const daysApplied = Array.isArray(t.daysApplied) && t.daysApplied.length ? t.daysApplied : [0, 1, 2, 3, 4, 5, 6];

          daysApplied.forEach((dayIndex) => {
            // 1) choose base date for the weekday (use getDateForWeekday semantics)
            const baseDate = getDateForWeekday(dayIndex); // returns Date object for that weekday in current week

            // 2) build start string using same composeDateString + getISODateStringFromDate as transformPlanResourceToEvent
            let startStr;
            if (!t.s) {
              // start is 00:00 same day
              startStr = composeDateString({
                dateString: getISODateStringFromDate(baseDate),
                timeString: "00:00",
              });
            } else {
              startStr = composeDateString({
                dateString: getISODateStringFromDate(baseDate),
                timeString: t.s,
              });
            }

            // 3) build end string; if end is empty -> next day 00:00
            let endStr;
            if (!t.e) {
              const nextDay = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + 1);
              endStr = composeDateString({
                dateString: getISODateStringFromDate(nextDay),
                timeString: "00:00",
              });
            } else {
              endStr = composeDateString({
                dateString: getISODateStringFromDate(baseDate),
                timeString: t.e,
              });
            }

            // 4) id must match SchedulerEvent ID format: `${serial}_${planTimeId}_${v}`
            const planTimeId = t.id || `preview_${Math.random().toString(36).slice(2)}`;
            const id = `${deviceSerial}_${planTimeId}_${t.v}`;

            // 5) resource id matches your transformPlanResourceToEvent usage: `${deviceSerial}_${v}`
            const resourceId = `${deviceSerial}_${t.v}`;

            // 6) create custom text (same logic as original)
            let customText = "";
            if (Array.isArray(t.daysApplied) && t.daysApplied.length < 7) {
              const activeNames = t.daysApplied.map((id) => initialDays.find((d) => d.id === id)?.fullName).join(", ");
              // format times similarly to transformPlanResourceToEvent
              const startLocal = new Date(startStr);
              const endLocal = new Date(endStr);
              customText = `${startLocal.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}-${endLocal.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })} (${activeNames})`;
            }

            /** @type {import("../types/global").SchedulerEvent} */
            const ev = {
              id: `${deviceSerial}_${planTimeId}_${t.v}`,
              resource: `${deviceSerial}_${t.v}`,
              start: startStr,
              end: endStr,
              status: t.status || "active",
              text: customText,
              moveVDisabled: true,
              isQuickPlan: !!t.isQuickPlan,
              isWeeklyPlan: true,
              readOnly: true,
              preview: true
            };

            results.push(ev);
          }); // daysApplied.forEach
        }); // plan.times.forEach
      }); // plan forEach
    }); // devices.forEach
    const filtered = results.filter((ev) => {
      try {
        const s = new Date(ev.start).getTime();
        const e = new Date(ev.end).getTime();
        return e > s; // keep only events with duration
      } catch {
        return true;
      }
    });

    return filtered;
  }

  const handleOnChangeDay = /** @type {import("../types/global").handleOnChangeDayFunction} */ (
    useCallback(
      (dayId) => () => {
        setDays(
          /** @type {import("react").SetStateAction<import("../types/global").Day[]>} */
          (
            (daysPreviousState) => {
              if (!garden?.schedulerMode) throw new Error(tr("Garden scheduler mode is missing"));

              const updatedDays = daysPreviousState.map((dayPreviousState) => ({
                ...dayPreviousState,
                selected: dayPreviousState.id === dayId ? !dayPreviousState.selected : dayPreviousState.selected,
              }));

              const nextSelected = updatedDays.filter((d) => d.selected).map((d) => d.id);

              // Compare against the saved state so reverting a toggle clears the change
              const saved = garden?.wateringDays ?? [];
              const isSameAsSaved =
                nextSelected.length === saved.length &&
                nextSelected.every((id) => saved.includes(id));

              setUncommittedChanges(
            /** @type {(prev: import("../types/global").UncommittedRequest[]) => import("../types/global").UncommittedRequest[]} */(prev) => {
                  // Always drop the previous watering-days entry first
                  const withoutDayChange = prev.filter((change) => change.type !== "update_watering_days");

                  // If we're back to the saved selection, don't re-add it
                  if (isSameAsSaved) return withoutDayChange;

                  return [
                    ...withoutDayChange,
                    {
                      type: "update_watering_days",
                      schedulerMode: garden?.schedulerMode || "advanced_common",
                      data: {
                        wateringDays: nextSelected,
                      },
                    },
                  ];
                }
              );

              return updatedDays;
            }
          )
        );
      },
      [garden?.schedulerMode, garden?.wateringDays, setUncommittedChanges]
    )
  );

  const {
    data: planResponses,
    isLoading: isLoadingPlans,
    isFetching: isFetchingPlans,
  } = /** @type {import("react-query").UseQueryResult<import("../types/global").DevicePlanResponse[]>} */ (
      useQuery(
        [
          "GardenDevicePlans",
          garden?.id,
          garden?.schedulerMode,
          (garden?.schedulerMode === "advanced_common" || garden?.schedulerMode === "quick")
            ? "weekly"
            : selectedDayId
        ],
        async () => {
          const isWeekly =
            garden?.schedulerMode === "advanced_common" ||
            garden?.schedulerMode === "quick";

          const schedulerMode =
            garden?.schedulerMode === "quick"
              ? schedulerModes.ADVANCED_COMMON
              : garden?.schedulerMode;

          let url = `${process.env.REACT_APP_PLANNER_API_END_POINT}/plan/garden?gardenId=${garden?.id}&bypassTemp=true`;
          url += `&mode=${schedulerMode}`;

          if (!isWeekly) {
            url += `&days=${selectedDayId}`;
          }

          const res = await axios.get(url);

          /** @type {import("../types/global").DevicePlanResponse[]} */
          const results =
            res?.data?.devices?.map((device) => ({
              model: device.model,
              serial: device.serial,
              plans: device.plans ?? [],
            })) ?? [];

          return results;
        },
        {
          enabled: !!garden?.schedulerMode && !!garden?.id,
        }
      )
    );

  const { mutate: updateSchedulerMode, isLoading: isUpdatingSchedulerMode } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").GardenApiResponse>>} */ (
      useMutation(
        /** @param {import("../types/global").UpdateSchedulerModeRequest} params*/
        ({ schedulerMode, selectedDay = 1 }) => {
          return axios.put(process.env.REACT_APP_PLANNER_API_END_POINT + "/garden", {
            id: gardenId,
            schedulerMode,
            selectedDay,
            crossSchedulePlans: true,
          });
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries("Garden");
          },
          onError: () => { },
        }
      )
    );

  /**
   * Apply the last previewed template to devices (calls existing PlansProvider API)
   * @returns {Promise<void>}
   */
  const applyPreview = useCallback(async () => {
    if (!lastPreviewedTemplateId) {
      console.warn("No previewed template id to apply");
      return;
    }

    try {
      // call PlansProvider's apply template action
      await onLoadGardenTemplate(lastPreviewedTemplateId);
      // after successful apply, exit preview mode
      setIsPreviewMode(false);
      setPreviewEvents(null);
    } catch (err) {
      console.error("Failed to apply preview", err);
    }
  }, [lastPreviewedTemplateId, onLoadGardenTemplate, queryClient]);

  const cancelPreview = useCallback(() => {
    setIsPreviewMode(false);
    setPreviewEvents(null);
  }, []);

  useEffect(() => {
    /** @type {import("../types/global").SchedulerEvent[]} */
    let events = [];
    for (const planResponse of planResponses || []) {
      /** @type {import("../types/global").SchedulerEvent[]} */
      const planEvents = transformPlanResourceToEvent(planResponse.plans, { model: planResponse.model, serial: planResponse.serial }, planResponse.model);
      events = [...events, ...planEvents];
    }
    setEvents(events);
  }, [planResponses]);

  useEffect(() => {
    if (garden?.wateringDays) {
      setDays((days) => days.map((day) => ({ ...day, selected: garden.wateringDays.includes(day.id) })));
    }
  }, [garden?.wateringDays]);

  useEffect(() => {
    setBltPlansEditable(JSON.parse(localStorage.getItem("setting") || "{}")?.manageBltPlansRemotely || false);
  }, []);

  useEffect(() => {
    setCellDuration((cellDurationPreviousState) => garden?.schedulerCellDuration || cellDurationPreviousState);
  }, [garden?.schedulerCellDuration]);

  useEffect(() => {
    const run = async () => {
      if (!lastPreviewedTemplateId) {
        queryClient.invalidateQueries("GardenDevicePlans");
        queryClient.invalidateQueries("GardenDevicePlansPerDay");
        setIsPreviewMode(false);
        return;
      }

      setIsPreviewMode(true);

      if (garden?.schedulerMode === "advanced_per_day" && selectedDayId) {
        setUncommittedChanges([]);

        await updateSchedulerMode({
          schedulerMode: schedulerModes.ADVANCED_COMMON,
        });

        setSelectedDayId(undefined);
      }
    };

    run();
  }, [lastPreviewedTemplateId]);

  // When PlansProvider sets gardenPlanTemplatePreview, convert it and enable preview mode
  useEffect(() => {
    if (!gardenPlanTemplatePreview) {
      // clear preview if not present
      setPreviewEvents(null);
      setIsPreviewMode(false);
      return;
    }

    try {
      const converted = convertGardenPreviewToEvents(gardenPlanTemplatePreview);
      setPreviewEvents(converted);
      setIsPreviewMode(true);
      // optionally scroll scheduler to start etc.
    } catch (err) {
      console.error("Failed converting garden template preview to events", err);
      setPreviewEvents(null);
      setIsPreviewMode(false);
    }
  }, [gardenPlanTemplatePreview]);

  const eventSchedulerContextValue = useMemo(() => {
    /** @type {import("../types/global").SchedulerDeviceResource[]} */
    const resources = transformDevicesToResourcesArray(devices, isBluetoothPlansEditable);

    /** @type {import("../types/global").EventSchedulerContextValue} */
    const value = {
      advancedSchedulerMode: garden?.schedulerMode !== "quick" ? garden?.schedulerMode : undefined,
      cellDurationState: [cellDuration, setCellDuration],
      daysState: [days, setDays],
      dayEventsState: [dayEvents, setDayEvents],
      eventsState: [events, setEvents],
      handleOnChangeDay,
      isBluetoothPlansEditable,
      resources,
      selectedDayIdState: [selectedDayId, setSelectedDayId],
      updateSchedulerMode,
      isLoadingPlans,
      isFetchingPlans,
      isPreviewMode,
      setIsPreviewMode,
      previewEventsState: [previewEvents, setPreviewEvents],
      applyPreview,
      cancelPreview,
    };

    return value;
  }, [
    cellDuration,
    dayEvents,
    days,
    devices,
    events,
    garden?.schedulerMode,
    handleOnChangeDay,
    isBluetoothPlansEditable,
    selectedDayId,
    updateSchedulerMode,
    isLoadingPlans,
    isFetchingPlans,
    isPreviewMode,
    previewEvents,
    applyPreview,
    cancelPreview,
  ]);

  if (process.env.NODE_ENV === "development") {
    window.days = days;
    window.events = events;
  }

  if (!gardenId) return null;

  return <EventSchedulerContext.Provider value={eventSchedulerContextValue}>{children}</EventSchedulerContext.Provider>;
}

/**
 * @param {import("react").PropsWithChildren<{disabled?: boolean; isLoading?: boolean}>} props
 * @returns {import("react").ReactElement | null}
 */
EventSchedulerProvider.ActiveDaysCommon = function EventSchedulerProviderDaySelector({ disabled = false, isLoading = false }) {
  const { advancedSchedulerMode, daysState, handleOnChangeDay, isPreviewMode } = useEventScheduler();
  const [days] = daysState;
  const { isFetchingGardenResponse, garden } = useGarden();

  if (advancedSchedulerMode !== "advanced_common" || advancedSchedulerMode !== "quick") return null;
  if (!garden) return null;

  return (
    <Row gutter={8} justify="center" wrap={false}>
      {days.map((day) => (
        <Col key={day.id} style={{ display: "flex", flex: 1 }}>
          {isLoading || isFetchingGardenResponse ? (
            <Skeleton.Button active style={{ borderRadius: 12, height: 24 }} />
          ) : (
            <Button
              disabled={disabled || isLoading || isFetchingGardenResponse}
              onClick={handleOnChangeDay(day.id)}
              size="small"
              style={{ flex: 1 }}
              type={day.selected ? "primary" : "default"}
            >
              {day.fullName}
            </Button>
          )}
        </Col>
      ))}
    </Row>
  );
};

/**
 * @returns {import("react").ReactElement | null}
 */
EventSchedulerProvider.ActiveDaysPerDay = function EventSchedulerProviderActiveDaysPerDay() {
  const { advancedSchedulerMode, updateSchedulerMode, daysState, handleOnChangeDay, selectedDayIdState, isPreviewMode } = useEventScheduler();
  const [days] = daysState;
  const [selectedDayId, setSelectedDayId] = selectedDayIdState;
  const { garden } = useGarden();
  const { uncommittedChangesState } = usePlans();
  const [uncommittedChages, setUncommittedChanges] = uncommittedChangesState;

  useEffect(() => {
    if (advancedSchedulerMode === schedulerModes.ADVANCED_COMMON || advancedSchedulerMode === schedulerModes.QUICK) {
      if (selectedDayId !== undefined) {
        setSelectedDayId(undefined);
      }
    }
  }, [advancedSchedulerMode]);

  // When scheduler changes to PER_DAY → ensure a day is selected
  useEffect(() => {
    if (advancedSchedulerMode === schedulerModes.ADVANCED_PER_DAY) {
      if (garden?.selectedDay != null) {
        setSelectedDayId(garden?.selectedDay);
      }
      else if (selectedDayId === undefined && days.length > 0) {
        setSelectedDayId(days[0].id);
      }
    }
  }, [advancedSchedulerMode, days]);

  if (!garden) return null;

  return (
    <List
      dataSource={days}
      grid={{ column: 7, gutter: 3 }}
      renderItem={(day, index) => {
        const isSelected = selectedDayId === day.id;

        return (
          <List.Item
            onClick={async () => {
              if (isPreviewMode) return;
              if (selectedDayId === undefined && day.id) {
                setSelectedDayId(day.id);
                await updateSchedulerMode({
                  schedulerMode: schedulerModes.ADVANCED_PER_DAY,
                  selectedDay: day.id
                });
                return;
              }
              if (day.id !== selectedDayId) {
                setSelectedDayId(day.id);
                await updateSchedulerMode({
                  schedulerMode: schedulerModes.ADVANCED_PER_DAY,
                  selectedDay: day.id
                });
                return;
              } else {
                await updateSchedulerMode({
                  schedulerMode: schedulerModes.ADVANCED_COMMON,
                });
                setSelectedDayId(undefined);
              }
            }}
            style={{
              backgroundColor: selectedDayId === day.id ? "#E0FAE1FF" : "#f9fafb",
              borderColor: selectedDayId === day.id ? "#27ae60" : "lightgray",
              borderStyle: "solid",
              borderWidth: 1,
              borderBottom: "none",
              borderRadius: `${index === 0 ? 8 : 0}px ${index === days.length - 1 ? 8 : 0}px 0px 0px`,
              cursor: "pointer",
              margin: 0,
              padding: "8px 6px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Space
              style={{
                width: "100%",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {day.fullName}
              {isSelected && (
                <Checkbox
                  className="green-checkbox"
                  checked={day.selected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={handleOnChangeDay(day.id)}
                  style={{
                    position: "absolute",
                    zIndex: 2,
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </Space>

            {!day.selected && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundImage: "repeating-linear-gradient(45deg, rgba(120, 120, 120, 0.3) 0 4px, transparent 2px 12px)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            )}
          </List.Item>
        );
      }}
      rowKey={(day) => String(day.id)}
      size="small"
    />
  );
};

/**
 * @returns {import("react").ReactElement | null}
 */
EventSchedulerProvider.EventScheduler = function EventSchedulerProviderEventScheduler() {
  const { cellDurationState, daysState, dayEventsState, eventsState, resources, selectedDayIdState, isLoadingPlans, isFetchingPlans, previewEventsState, isPreviewMode } =
    useEventScheduler();
  const [cellDuration, setCellDuration] = cellDurationState;
  const [days] = daysState;
  const [selectedDayId] = selectedDayIdState;
  const [dayEvents, setDayEvents] = dayEventsState;
  const [events, setEvents] = eventsState;
  const [previewEvents, setPreviewEvents] = previewEventsState;
  const { garden, gardenId, mutateGarden } = useGarden();
  const { uncommittedChangesState } = usePlans();
  const [uncommittedChanges, setUncommittedChanges] = uncommittedChangesState;
  const { devices } = useDevice();

  /**
   * @param {string} value
   * @return {void}
   */
  const onCellDurationChanged = useCallback(
    (value) => {
      console.count("onCellDurationChanged called");

      setCellDuration(parseInt(value));

      if (!gardenId) return;

      /** @type {import("../types/global").GardenUpdateRequest} */
      const gardenUpdateRequest = {
        id: gardenId,
        schedulerCellDuration: parseInt(value),
      };

      mutateGarden(gardenUpdateRequest);
    },
    [gardenId, mutateGarden, setCellDuration]
  );

  const config = useMemo(
    () => ({
      AllowEventOverlap: false,
      bubble: new DayPilot.Bubble({
        onLoad: (args) => {
          args.html = WateringMapper(args);
        },
      }),
      days: isPreviewMode ? 7 : 1,
      eventClickHandling: "Disabled",
      eventDeleteHandling: "Update",
      eventHoverHandling: "Bubble",
      eventMoveHandling: "Update",
      eventResizeHandling: "Update",
      groupConcurrentEvents: false,
      // @ts-ignore
      onBeforeEventRender: (args) => {
        args.data.barColor = "#5FA600";
      },
      // @ts-ignore
      onBeforeTimeHeaderRender: async function (args) {
        if (args.header.level === 1) {
          if (args.header.text.length === 1) {
            args.header.html = `0${args.header.text}`;
          }
        }
        if (args.header.level === 0) {
          args.header.html = "TEST";
        }
      },
      onEventDeleted: /** @type {import("../types/global").onEventDeletedFunction} */ (args) => {
        // Remove from uncommitted changes
        setUncommittedChanges((prev) => prev.filter((e) => !("id" in e) || e.id !== args.e.data.id));
        const dayId = garden?.schedulerMode === "advanced_per_day" ? selectedDayId : null;

        // Append delete plan if not new
        if (args.e.data.status !== "new") {
          if (!garden) return;
          setUncommittedChanges((prev) => [...prev,
          {
            data: deletePlanRequestBuilder(args, dayId),
            type: "delete_plan",
            schedulerMode: garden.schedulerMode,
            meta: { isQuickPlan: !!args.e.data.isQuickPlan, resource: args.e.data.resource },
          }]);
        }

        // Update events
        setEvents((prev) =>
          prev.map((e) => {
            if (e.id !== args.e.data.id) return e;

            // DayEventItem (uncommitted / new)
            if ("status" in e && e.status === "new") {
              return { ...e, status: "deleted" }; // keep as DayEventItem
            }

            // SchedulerEvent (saved)
            if ("status" in e && (e.status === "active" || e.status === "deleted" || e.status === "disable")) {
              // return a new SchedulerEvent-shaped object, but mark as deleted in a way DayPilot notices
              e.status = "deleted";
              return e;
            }

            return e;
          })
        );

        // Update day events
        setDayEvents((prev) => {
          const safePrev = prev ?? {};
          const dayEventsForDay = safePrev[selectedDayId] ?? [];
          return {
            ...safePrev,
            [selectedDayId]: dayEventsForDay.map((e) => (e.id === args.e.data.id ? { ...e, status: "deleted" } : e)),
          };
        });
      },
      onTimeRangeSelected: /** @type {import("../types/global").onTimeRangeSelectedFunction} */ (
        async (args) => {
          if (!garden) return;

          const targetDays =
            garden.schedulerMode === "advanced_per_day" ? [selectedDayId] : days.filter((d) => d.selected).map((d) => d.id);

          /** @type {ReturnType<import("../types/global").createPlanRequestBuilderFunction>} */
          const planData = createPlanRequestBuilder(
            args,
            garden.schedulerMode === "advanced_per_day" ? selectedDayId : days.map((d) => d.id),
            `Advanced plan (${garden.schedulerMode})`
          );

          setUncommittedChanges((prev) => [...prev, { data: planData, id: args.id, schedulerMode: garden.schedulerMode, type: "create_plan", meta: { targetDays, resource: args.resource } }]);

          setEvents(
            /** @type {import("react").SetStateAction<(import("../types/global").SchedulerEvent | import("../types/global").DayEventItem)[]>} */
            (prev) => [...prev, { ...args, status: "new" }]
          );

          // we need to manage new events per day in order to persist through day-tab changes
          setDayEvents((prev) => {
            const safePrev = prev ?? {}; // <-- handle undefined

            const dayEventsForDay = safePrev[selectedDayId] ?? [];

            return {
              ...safePrev,
              [selectedDayId]: [...dayEventsForDay, { ...args, status: "new" }],
            };
          });
        }
      ),
      locale: "tr-tr",
      scale: "CellDuration",
      startDate: DayPilot.Date.today(),
      timeHeaders: [{ groupBy: "Day", format: "dddd, d MMMM yyyy" }, { groupBy: "Hour" }, { groupBy: "Cell", format: "mm" }],
      timeRangeSelectedHandling: "Enabled",
      treeEnabled: true,
      treePreventParentUsage: true,
    }),
    [days, garden, selectedDayId, setDayEvents, setEvents, setUncommittedChanges]
  );

  if (!devices.length) return null;

  if (process.env.NODE_ENV === "development") {
    window.uncommittedChanges = uncommittedChanges;
  }

  return (
    <div style={{ position: "relative" }}>
      <Space style={{ position: "absolute", right: 0, top: 63, zIndex: 100 }}>
        <Typography.Text type="secondary">{tr("Zoom level")}</Typography.Text>

        <Select suffixIcon={<ZoomInOutlined />} size="small" value={cellDuration} onChange={onCellDurationChanged} style={{ width: 60 }}>
          <Select.Option key="1" value="1">
            1 {tr("Mins")}
          </Select.Option>

          <Select.Option key="2" value="2">
            2 {tr("Mins")}
          </Select.Option>

          <Select.Option key="5" value="5">
            5 {tr("Mins")}
          </Select.Option>

          <Select.Option key="10" value="10">
            10 {tr("Mins")}
          </Select.Option>

          <Select.Option key="15" value="15">
            15 {tr("Mins")}
          </Select.Option>

          <Select.Option key="30" value="30">
            30 {tr("Mins")}
          </Select.Option>
        </Select>
      </Space>

      <Spin spinning={isLoadingPlans || isFetchingPlans} indicator={<Loading3QuartersOutlined spin style={{ fontSize: 32 }} />} tip={tr("Loading schedule data")}>
        <EventSchedulerControl
          {...config}
          cellDuration={cellDuration}
          events={isPreviewMode && previewEvents ? previewEvents : events}
          resources={resources}
          selectedDayId={selectedDayId}
          selectedDay={selectedDayId !== undefined ? initialDays.find((d) => d.id === selectedDayId)?.fullName + " " + tr("Plans of") : tr("Weekly Schedule")}
          isPreviewMode={isPreviewMode}
        />
      </Spin>

      {garden?.schedulerMode === "advanced_per_day" &&
        days
          .filter((d) => !d.selected)
          .map((d) => d.id)
          .includes(selectedDayId) && (
          <div
            style={{
              backgroundImage: "repeating-linear-gradient(45deg, rgba(120, 120, 120, 0.3) 0 8px, transparent 2px 20px)",
              height: "100%",
              left: 0,
              position: "absolute",
              top: 0,
              width: "100%",
              zIndex: 101,
            }}
          ></div>
        )}
    </div>
  );
};

/**
 * @returns {import("react").ReactElement | null}
 */
EventSchedulerProvider.UncommittedChangesAlert = function EventSchedulerProviderUncommittedChangesAlert() {
  const { commitSchedulesAsync, isCommittingSchedules, uncommittedChangesState } = usePlans();
  const { gardenId } = useGarden();
  const { daysState, eventsState } = useEventScheduler();
  const [days] = daysState;
  const [events] = eventsState;
  const [uncommittedChages, setUncommittedChanges] = uncommittedChangesState;
  const [reviewOpen, setReviewOpen] = useState(false);

  if (!gardenId) return null;
  if (uncommittedChages.length === 0) return null;

  const formatEventTime = (d) =>
    new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });

  const handleReviewClick = () => {
    const found = findPlanConflicts(events);
    if (found.length > 0) {
      notification.error({
        message: tr("Overlapping plans detected"),
        description: (
          <div style={{ fontSize: 13 }}>
            {tr("These plans overlap on the same valve and must be resolved before saving")}:
            <ul style={{ margin: "6px 0 0", paddingInlineStart: 18 }}>
              {found.map((c, i) => (
                <li key={i}>
                  {formatEventTime(c.a.start)}–{formatEventTime(c.a.end)}
                  {" ↔ "}
                  {formatEventTime(c.b.start)}–{formatEventTime(c.b.end)}
                  {" "}({daysLabel(c.days)})
                </li>
              ))}
            </ul>
          </div>
        ),
      });
      return;
    }
    setReviewOpen(true);
  };

  const doCommit = async () => {
    await commitSchedulesAsync({ commits: uncommittedChages, gardenId });
    setUncommittedChanges([]);
    setReviewOpen(false);
  };

  return (
    <>
      <Alert
        action={
          <Button
            danger
            disabled={isCommittingSchedules}
            icon={isCommittingSchedules ? <Loading3QuartersOutlined spin /> : null}
            onClick={handleReviewClick}    // <-- was () => setReviewOpen(true)
            type="primary"
          >
            {isCommittingSchedules ? `${tr("Saving")}...` : tr("Save")}
          </Button>
        }
        description={tr("There are unsaved changes to the watering schedule")}
        showIcon banner
        icon={<WarningFilled style={{ color: "danger" }} />}
        style={{ borderRadius: 8, lineHeight: 1, padding: "4px 12px", gap: 8, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between" }}
        type="error"
      />

      <CommitReviewModal
        open={reviewOpen}
        onConfirm={doCommit}
        onCancel={() => setReviewOpen(false)}
        confirmLoading={isCommittingSchedules}
        changes={uncommittedChages}
        days={days}
      />
    </>
  );
};

EventSchedulerProvider.SchedulerModeTitle = function EventSchedulerProviderSchedulerModeTitle() {
  const { isPreviewMode, selectedDayIdState } = useEventScheduler();
  const [selectedDayId] = selectedDayIdState;

  const bgColor = isPreviewMode
    ? "#f9fafb"
    : selectedDayId !== null && selectedDayId !== undefined
      ? "#E0FAE1"
      : "#f9fafb";

  const title =
    isPreviewMode
      ? tr("Preview Mode")
      : selectedDayId !== undefined
        ? `${initialDays.find((d) => d.id === selectedDayId)?.fullName} ${tr("Plans of")}`
        : tr("Weekly Schedule");

  return (
    <Row
      style={{
        width: "100%",
        border: "1px solid #e8e8e8",
        padding: "4px 16px",
        backgroundColor: bgColor,
        textAlign: "center"
      }}
    >
      <Col span={24}>
        <h2 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 600 }}>
          {title}
        </h2>
      </Col>
    </Row>
  );
};